import type { GenerationEpoch, SolverEpoch } from './types';

/**
 * Grid numbers outlive the code that makes them.
 *
 * "The same date produces the same grid on every device, forever" (GDD §4.1)
 * is a promise about the past as well as the future: grid #12 has to deal the
 * map it dealt in January, and the archive result saved against it has to
 * still mean something. So neither the generator nor the reference search is
 * allowed to change under a grid number that already exists. Both are
 * versioned instead, and a version applies to a range of grid numbers.
 *
 * Adding a version is always the same move: implement it alongside the old
 * one, then open its range at a grid number safely past the release. Never
 * widen an existing range downward, and never edit an epoch's code — an epoch
 * is a frozen artifact, not a branch of the current implementation.
 */
interface EpochRange<T> {
  /** First grid number this version deals. */
  from: number;
  version: T;
}

/**
 * Epoch 1 is the five archetypes and the flatness floor (GDD §5), and it
 * covers every grid number: the game had no players when it opened, so there
 * was no history to protect and no reason to leave the archive dealing an
 * older shape.
 *
 * That is the last time a range starts at 1. The next generator opens at a
 * grid number set weeks past its release, because by then grids will have been
 * played and shared, and re-dealing one would replace a map somebody holds a
 * result for. The gap is the release's margin, not a formality: the PWA
 * updates on next launch, so a boundary that passes before every player has
 * the new build splits that day between two generators.
 */
const GENERATION: readonly EpochRange<GenerationEpoch>[] = [{ from: 1, version: 1 }];

/**
 * Epoch 1 is the multi-start hill climb the game shipped with. There is only
 * one so far; the registry exists so that improving the search is a safe move
 * rather than a silent rewrite of every target in the archive.
 */
const SOLVER: readonly EpochRange<SolverEpoch>[] = [{ from: 1, version: 1 }];

/** The newest generator. Used where there is no grid number, as in practice. */
export const LATEST_GENERATION_EPOCH: GenerationEpoch = 1;

/** The newest reference search. Used where there is no grid number. */
export const LATEST_SOLVER_EPOCH: SolverEpoch = 1;

function versionFor<T>(ranges: readonly EpochRange<T>[], day: number, latest: T): T {
  let found = latest;
  for (const range of ranges) {
    if (day >= range.from) found = range.version;
  }
  return ranges.length && day < ranges[0].from ? ranges[0].version : found;
}

/** Which generator deals a given daily grid. */
export function generationEpochFor(day: number): GenerationEpoch {
  return versionFor(GENERATION, day, LATEST_GENERATION_EPOCH);
}

/** Which reference search targets a given daily grid. */
export function solverEpochFor(day: number): SolverEpoch {
  return versionFor(SOLVER, day, LATEST_SOLVER_EPOCH);
}

/** The first grid number an epoch applies to, for tests and tooling. */
export function generationEpochStart(version: GenerationEpoch): number {
  return GENERATION.find((r) => r.version === version)?.from ?? 1;
}
